import { Injectable, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import {
  dateRangeValidator,
  minLengthWithMessage,
  numberRangeValidator,
} from 'src/app/shared/validators/custom-validators';

export interface ListFilters {
  document?: string;
  name?: string;
  segment?: string;
  internalCode?: string;
  dateRange?: { start?: Date; end?: Date };
  scoreRange?: { min?: number; max?: number };
  restrictivesRange?: { min?: number; max?: number };
}
export type FilterFieldNames = keyof ListFilters;

@Injectable()
export class ListFilterService implements OnDestroy {
  private readonly filterForm: FormGroup;
  private readonly filterValues = new BehaviorSubject<ListFilters | null>(null);

  public readonly filterValues$ = this.filterValues.asObservable();

  constructor(private readonly fb: FormBuilder) {
    this.filterForm = this.fb.group({
      document: [''],
      name: ['', minLengthWithMessage(3)],
      dateRange: this.fb.group(
        {
          start: [null],
          end: [null],
        },
        { validators: dateRangeValidator('start', 'end') },
      ),
      scoreRange: this.fb.group(
        {
          min: [null],
          max: [null],
        },
        { validators: numberRangeValidator('min', 'max', 0, 1000) },
      ),
      restrictivesRange: this.fb.group(
        {
          min: [null],
          max: [null],
        },
        { validators: numberRangeValidator('min', 'max') },
      ),
      internalCode: [''],
      segment: [''],
    });
  }

  getForm(): FormGroup {
    return this.filterForm;
  }

  getCurrentFiltersValue(): ListFilters | null {
    return this.filterValues.getValue();
  }

  applyFilters() {
    if (this.filterForm.valid) {
      const formValue = this.filterForm.value;

      if (formValue?.document?.trim() === '') {
        formValue.document = null;
      }

      this.filterValues.next(formValue);
    }
  }

  applyASingleFilter(fieldName: FilterFieldNames) {
    const value = this.filterForm.get(fieldName)?.value;
    const currentFilters = this.filterValues.value ?? {};
    const updatedFilters = { ...currentFilters, [fieldName]: value };
    this.filterValues.next(updatedFilters);
  }

  clearFilters() {
    this.filterForm.reset();
    this.applyFilters();
  }

  clearFilterField(fieldName: keyof ListFilters) {
    this.filterForm.get(fieldName)?.reset();
    this.applyFilters();
  }

  ngOnDestroy(): void {
    this.filterValues.complete();
  }
}
